import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { onAuthStateChanged } from "firebase/auth";
import { firestore, auth, storage, functions } from '../firebase';
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import SubscriptionModal from './SubscriptionModal';
import axios from "axios";
import Handlebars from "handlebars";
import { httpsCallable } from "firebase/functions";
import DOMPurify from 'dompurify';
import { fetchParsedResumeText, handleResumeUpload } from './handleResume';
import HtmlDocx from 'html-docx-js/dist/html-docx';
import { saveAs } from 'file-saver';
import { useUser } from '../contexts/UserContext';
 
import './CoverLetterBuilder.css';

const CoverLetterBuilder = () => {
  // State definitions
  const [userId, setUserId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [currentResume, setCurrentResume] = useState(null);
  const [jobDescriptionText, setJobDescriptionText] = useState("");
  const [parsedJobDescription, setParsedJobDescription] = useState("");
  const [coverLetterContent, setCoverLetterContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { onSignupOpen } = useUser();

  // ─── HOOK #1: track auth state ─────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !user.isAnonymous) {
        setUserId(user.uid);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    });
    return () => unsubscribe();
  }, []);

    // ─── HOOK #2: Fetch templates on step 1 (always) ───────────────────────
    useEffect(() => {
      if (step !== 1) return;
  
      const fetchTemplates = async () => {
        try {
          const col = collection(firestore, 'cover_letters');
          const snap = await getDocs(col);
          const fetched = await Promise.all(
            snap.docs.map(async (d) => {
              const data = d.data();
              let preview_url = null;
              try {
                preview_url = await getDownloadURL(
                  ref(storage, `/cover_letter_template_previews/${d.id}.png`)
                );
              } catch { /* no preview */ }
              return { id: d.id, ...data, preview_url };
            })
          );
          setTemplates(fetched);
        } catch (err) {
          console.error("Error fetching templates:", err);
        }
      };
      fetchTemplates();
    }, [step]);

  // Function to handle resume selection (Step 2)
  const handleResumeSelection = async (useCurrentResume) => {
    setLoading(true);
    setProgress('Fetching resume...');
    try {
      if (useCurrentResume) {
        const resume = await fetchParsedResumeText(userId);
        if (!resume) throw new Error("No current resume found.");
        setCurrentResume(resume);
        setStep(3); // Move to Step 3
      } else {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.pdf,.docx';
        fileInput.onchange = async (event) => {
          const file = event.target.files[0];
          if (file) {
            setLoading(true);
            setProgress('Uploading resume...');
            const resumeUrl = await handleResumeUpload(file, userId);
            setProgress('Parsing resume...');
            const parsedData = await parseFile(resumeUrl, file.type);
            if (!parsedData) throw new Error("Failed to parse the resume.");
            const userDocRef = doc(firestore, "uploads", userId, "parsedDocuments", "parsedResume");
            await setDoc(userDocRef, {
              type: "resume",
              content: parsedData,
              timestamp: new Date(),
            });
            setCurrentResume(parsedData);
            setStep(3);
          }
        };
        fileInput.click();
      }
    } catch (error) {
      console.error("Error selecting resume:", error);
      alert(error.message);
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  // Parse file function (used for both resume and job description)
  const parseFile = async (fileUrl, fileType) => {
    try {
      const response = await axios.post("https://api-whiywkgf4q-uc.a.run.app/parseFile", { url: fileUrl });
      return response.data.text;
    } catch (error) {
      console.error("Error parsing file:", error.message);
      return null;
    }
  };

  // Handler for job description file upload (Step 3)
  const handleJobDescriptionFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setLoading(true);
      setProgress('Parsing job description file...');
      const fileUrl = await handleResumeUpload(file, userId);
      const parsedText = await parseFile(fileUrl, file.type);
      if (parsedText) {
        setParsedJobDescription(parsedText);
      } else {
        alert("Failed to parse job description file.");
      }
      setLoading(false);
      setProgress('');
    }
  };

  // Generate cover letter (Step 4)
  const generateCoverLetter = async () => {
    if (!currentResume) {
      alert("Please provide your resume.");
      return;
    }
    // Use parsed text if available; otherwise, use the pasted job description
    const jobDesc = parsedJobDescription || jobDescriptionText;
    if (!jobDesc || jobDesc.length < 100) {
      alert("Please provide a valid job description or position advertisement (at least 100 characters).");
      return;
    }
    setLoading(true);
    setProgress("Generating cover letter...");
    try {
      const safeJobDesc = DOMPurify.sanitize(jobDesc);
      const requestData = {
        resume: currentResume,
        message: safeJobDesc,
        rule: "coverLetter"
      };
      const callableFunction = httpsCallable(functions, 'generateContent');
      const response = await callableFunction(requestData);
      if (response.data.status === "LIMIT_REACHED") {
        setShowSubscriptionModal(true);
        setLoading(false);
        return;
      }
        // === Simplified: grab GPT output and feed it straight into your template ===
        const gptOutput = response.data.content;

        // Optional: convert markdown bold / headings / linebreaks to HTML
        const contentHtml = gptOutput
        .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
        .replace(/### (.*?)\n/g, '<h3>$1</h3>\n')
        .replace(/\n/g, '<br />');

        try {
        // Fetch your chosen Handlebars template from Storage
        const tmplRef = ref(
            storage,
            `/cover_letters/${selectedTemplate}.html`
        );
        const tmplUrl = await getDownloadURL(tmplRef);
        const tmplResp = await fetch(tmplUrl);
        const tmplText = await tmplResp.text();

        // Compile and inject
        const template = Handlebars.compile(tmplText);
        const finalHtml = template({
            coverLetterContent: contentHtml,
            // you can still pass an applicantName if you like:
            applicantName: ''
        });

        setCoverLetterContent(finalHtml);
        } catch (err) {
        console.error("Template load/compile failed, falling back:", err);
        // fallback to raw content
        setCoverLetterContent(contentHtml);
        }

        setStep(4);

    } catch (error) {
      console.error("Error generating cover letter:", error);
      alert("An error occurred while generating your cover letter. Please try again.");
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  // ─── STEP 1 ⇒ 2: Template click ─────────────────────────────────────────
  const handleTemplateSelect = (templateId) => {
    if (!isAuthenticated) {
      setShowSubscriptionModal(true);
      return;
    }
    setSelectedTemplate(templateId);
    setStep(2);
  };

  const handleRequireSignup = () => {
    onSignupOpen();
    setShowSubscriptionModal(false);
  };

  // Copy cover letter text to clipboard (Step 4)
  const copyToClipboard = () => {
    navigator.clipboard.writeText(coverLetterContent)
      .then(() => alert("Cover letter copied to clipboard!"))
      .catch(() => alert("Failed to copy text."));
  };

  // Download cover letter as a Word document (Step 4)
  const downloadCoverLetter = () => {
  // Wrap your full HTML document or just the body fragment:
  const fullHtml = `
    <html><head><meta charset="utf-8"></head><body>
      ${coverLetterContent}
    </body></html>
  `;
  // Convert to a Blob containing a real .docx package
  const docxBlob = HtmlDocx.blob(fullHtml, {
    orientation: 'portrait',
    margins: { top: 1440, right: 1440, bottom: 1440, left: 1440 } // in twentieths of a point
  });
  saveAs(docxBlob, 'CoverLetter.docx');
  };

  return (
    <div className="cover-letter-builder">
      {loading && <div className="loading-indicator"><p>{progress}</p></div>}

      {/* Subscription portal */}
      {showSubscriptionModal &&
        ReactDOM.createPortal(
          <div className="subscription-overlay">
            <div className="subscription-modal">
              <button
                className="modal-close"
                onClick={() => setShowSubscriptionModal(false)}
              >
                ×
              </button>
              <SubscriptionModal
                isOpen={true}
                onClose={() => setShowSubscriptionModal(false)}
                onSubscriptionSuccess={() => setShowSubscriptionModal(false)}
                onRequireSignup={handleRequireSignup}
                productType="coverLetter"
              />
            </div>
          </div>,
          document.getElementById('modal-root')
        )
      }


      {step === 1 && (
        <div className="step step-1">
          <h2>Select a Cover Letter Template</h2>
          <div className="template-grid">
            {templates.map((template) => (
              <div
                key={template.id}
                className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                onClick={() => handleTemplateSelect(template.id)}
              >
                {template.preview_url ? (
                  <img src={template.preview_url} alt={template.name} />
                ) : (
                  <div className="no-preview">No Preview</div>
                )}
                <p>{template.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="step step-2">
          <h2>Select Your Resume</h2>
          <div className="button-group">
            <button onClick={() => handleResumeSelection(true)}>Use Current Resume</button>
            <button onClick={() => handleResumeSelection(false)}>Upload Resume</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="step step-3">
          <h2>Provide Job Advert / Position Description</h2>
          <div className="job-description-options">
            <div className="job-description-paste">
              <p>Or Paste Text:</p>
              <textarea
                value={jobDescriptionText}
                onChange={(e) => setJobDescriptionText(e.target.value)}
                placeholder="Paste the job advert or position description here..."
              />
            </div>
            <div className="job-description-upload">
              <p>Upload File:</p>
              <input type="file" accept=".pdf,.docx" onChange={handleJobDescriptionFileUpload} />
            </div>
          </div>
          <button className="next-button" onClick={generateCoverLetter}>
            Generate Cover Letter
          </button>
        </div>
      )}

        {step === 4 && coverLetterContent && (
        <div className="step step-4">
            <h2>Your Generated Cover Letter</h2>
            <iframe
            className="cover-letter-iframe"
            title="Cover Letter Preview"
            srcDoc={coverLetterContent}
            sandbox="allow-same-origin allow-popups allow-forms"
            />
            <div className="action-buttons">
            <button onClick={downloadCoverLetter}>Download as Word</button>
            </div>
        </div>
        )}
    </div>
  );
};

export default CoverLetterBuilder;
